import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { AgentTraceListPane } from '@apps/web/routes/_app/admin/agent-traces/agent_trace_list_pane';
import { agentTracesSearchSchema } from '@apps/web/routes/_app/admin/agent-traces/search_params';
import { SandboxGraphs } from '@apps/web/routes/_app/admin/sandbox/sandbox_graphs';

export const Route = createFileRoute('/_app/admin/sandbox/')({
    head: () => ({ meta: [{ title: 'Sandbox Explorer | Phoebe Admin' }] }),
    component: AdminSandboxPage,
    validateSearch: agentTracesSearchSchema.extend({
        tab: z.enum(['runs', 'graphs']).default('runs'),
        days: z.union([z.literal(1), z.literal(7), z.literal(30)]).default(7),
        organizationId: z.string().optional(),
    }),
});

function AdminSandboxPage() {
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    return (
        <div className="bg-admin-canvas text-admin-ink flex flex-col gap-5 p-6">
            <header>
                <h1 className="text-2xl font-semibold tracking-tight">
                    Sandbox explorer
                </h1>
                <p className="text-admin-ink-muted mt-1 text-sm">
                    Find a sandbox run, read its transcript, and inspect its
                    files.
                </p>
            </header>
            <Tabs
                value={search.tab}
                onValueChange={(tab) => {
                    if (tab === 'runs' || tab === 'graphs') {
                        void navigate({
                            search: (current) => ({ ...current, tab }),
                        });
                    }
                }}
            >
                <TabsList aria-label="Sandbox views">
                    <TabsTrigger value="runs">Runs</TabsTrigger>
                    <TabsTrigger value="graphs">Graphs</TabsTrigger>
                </TabsList>
                <TabsContent value="runs" className="pt-4">
                    <AgentTraceListPane
                        fromSandbox={true}
                        search={{ ...search, sandbox_only: true }}
                        onSearchChange={(updates, options) => {
                            void navigate({
                                search: (current) => ({
                                    ...current,
                                    ...updates,
                                }),
                                replace: options?.replace,
                            });
                        }}
                    />
                </TabsContent>
                <TabsContent value="graphs">
                    <SandboxGraphs
                        days={search.days}
                        organizationId={search.organizationId}
                        onChange={(updates) => {
                            void navigate({
                                search: (current) => ({
                                    ...current,
                                    ...updates,
                                }),
                            });
                        }}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
